/**
 * Modal to create a user under a given account. Minimal UI for admin user
 * provisioning (username / email / password / role).
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { RootState, AppDispatch } from '../../store';
import { createAccountUser, clearAccountsError } from '../../store/accountsSlice';
import { UserRole } from '../../types/auth';

interface AddUserModalProps {
    show: boolean;
    onHide: () => void;
    accountId: string;
    accountName: string;
}

const ROLE_OPTIONS: UserRole[] = ['client', 'gamemaster', 'admin'];

const AddUserModal: React.FC<AddUserModalProps> = ({
    show,
    onHide,
    accountId,
    accountName,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const error = useSelector((state: RootState) => state.accounts.error);

    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<UserRole>('client');
    const [validated, setValidated] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (show) {
            setUsername('');
            setEmail('');
            setRole('client');
            setValidated(false);
            dispatch(clearAccountsError());
        }
    }, [show, dispatch]);

    const handleClose = () => {
        dispatch(clearAccountsError());
        onHide();
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (form.checkValidity() === false) {
            event.stopPropagation();
            setValidated(true);
            return;
        }
        setValidated(true);
        setSubmitting(true);
        try {
            await dispatch(
                createAccountUser({
                    accountId,
                    data: {
                        username: username.trim(),
                        email: email.trim(),
                        role,
                    },
                })
            ).unwrap();
            handleClose();
        } catch {
            // Error surfaced via the redux `error` state and the Alert below.
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Add User to {accountName}</Modal.Title>
            </Modal.Header>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => dispatch(clearAccountsError())}
                        >
                            {error}
                        </Alert>
                    )}

                    <Form.Group className="mb-3" controlId="userUsername">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            minLength={1}
                            maxLength={32}
                            disabled={submitting}
                            autoFocus
                        />
                        <Form.Control.Feedback type="invalid">
                            Please enter a username (1-32 characters).
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="userEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="Enter email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={submitting}
                        />
                        <Form.Control.Feedback type="invalid">
                            Please enter a valid email address.
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="userRole">
                        <Form.Label>Role</Form.Label>
                        <Form.Select
                            value={role}
                            onChange={(e) => setRole(e.target.value as UserRole)}
                            disabled={submitting}
                        >
                            {ROLE_OPTIONS.map((r) => (
                                <option key={r} value={r}>
                                    {r.charAt(0).toUpperCase() + r.slice(1)}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Alert variant="info" className="mb-0">
                        The user will receive an email with a time-limited link to set
                        their own password. They can&apos;t sign in until they do.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={submitting}>
                        {submitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Sending invite...
                            </>
                        ) : (
                            'Create & Send Invite'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AddUserModal;
